import { useState, useEffect } from "react";

/**
 * True below Bootstrap's `sm` breakpoint — the width at which results switch
 * from the desktop grid to the card layout.
 *
 * This has to be a JS branch rather than a media query. `App` renders
 * `shown = isOpen ? group.images : [group.cover]`, so a collapsed group's other
 * images are NOT in the DOM for CSS to reveal — the card layout needs them
 * rendered, and the grid must not pay for hundreds of extra nodes it keeps
 * hidden.
 *
 * 575.98 rather than 576 mirrors Bootstrap's own `media-breakpoint-down`, which
 * subtracts 0.02px so `down(xs)` and `up(sm)` cannot both match at a fractional
 * viewport width.
 *
 * Guarded on `typeof window.matchMedia === "function"` the same way
 * resources/js/locationLibrary/App.js does.
 */
const PHONE_QUERY = "(max-width: 575.98px)";

export default function useIsPhone() {
    const supported = typeof window !== "undefined"
        && typeof window.matchMedia === "function";

    const [isPhone, setIsPhone] = useState(
        () => (supported ? window.matchMedia(PHONE_QUERY).matches : false)
    );

    useEffect(() => {
        if (!supported) return;

        const mql = window.matchMedia(PHONE_QUERY);
        const onChange = (e) => setIsPhone(e.matches);

        // Safari below 14 has no addEventListener on MediaQueryList; this app
        // still supports older iOS, which is precisely the audience for the
        // phone layout, so the legacy path is not optional here.
        if (typeof mql.addEventListener === "function") {
            mql.addEventListener("change", onChange);
            return () => mql.removeEventListener("change", onChange);
        }

        mql.addListener(onChange);
        return () => mql.removeListener(onChange);
    }, [supported]);

    return isPhone;
}
